/**
 * The arithmetic of the door editor (D31): where a pointer is on the door, which of number and
 * logo it reaches for, and how far an arrow key moves either. Positions are whole per cents of
 * the door, the unit the backend stores and bounds, so nothing here can produce a value the
 * API would refuse.
 */

export interface DoorPoint {
  readonly x: number;
  readonly y: number;
}

export interface DoorRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

const clampPercent = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

/** A pointer position as whole per cents of the door, held inside it. */
export function pointToPercent(rect: DoorRect, clientX: number, clientY: number): DoorPoint {
  if (rect.width <= 0 || rect.height <= 0) {
    return { x: 50, y: 50 };
  }
  return {
    x: clampPercent(((clientX - rect.left) / rect.width) * 100),
    y: clampPercent(((clientY - rect.top) / rect.height) * 100),
  };
}

/** Which of the two the pointer is closer to; the number on a tie, it is the smaller target. */
export function nearer(point: DoorPoint, number: DoorPoint, logo: DoorPoint): "number" | "logo" {
  const distance = (other: DoorPoint): number => Math.hypot(point.x - other.x, point.y - other.y);
  return distance(number) <= distance(logo) ? "number" : "logo";
}

const STEPS: Readonly<Record<string, DoorPoint>> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

/** The position after an arrow key, `step` per cent at a time; `null` for any other key. */
export function nudge(point: DoorPoint, key: string, step: number): DoorPoint | null {
  const direction = STEPS[key];
  if (direction === undefined) {
    return null;
  }
  return {
    x: clampPercent(point.x + direction.x * step),
    y: clampPercent(point.y + direction.y * step),
  };
}
